// IABF event / year configuration.
//
// Boundary rule: this app shares a Firebase project (Auth + Firestore) with
// brocheballetonline.com. ONLY collections and storage paths prefixed with
// `iabf-` belong to the festival. Never read or write non-iabf collections
// for festival features.
//
// Phase 2: all year-scoped collection names go through helpers here.
// No deletes of legacy collections — copy-migrations only.

/**
 * Festival year the dancer app targets today.
 * Travel, dietary, etc. are year-scoped — a 2027 enrollment starts with empty
 * travel (not 2026 trip details).
 */
export const IABF_DANCER_YEAR = 2027

/** Year new public registrations land in (same as dancer year once open). */
export const IABF_REGISTRATION_YEAR = 2027

/**
 * Every year the admin year switcher can select.
 * Includes early years that only have a dancers roster (via people / legacy
 * collections), not full festival ops modules.
 */
export const IABF_ADMIN_YEARS = [2022, 2023, 2024, 2025, 2026, 2027]

/**
 * Years with full admin surface (guests, store, carpools, itinerary, …).
 * 2026 = wrap-up; 2027 = registration / next festival.
 */
export const IABF_OPERABLE_YEARS = [2026, 2027]

/**
 * Archive years: admin can browse the dancers list only (no other modules).
 * 2022–2023 live on iabf-people.yearsAttended; 2024–2025 have enrollment
 * collections.
 */
export const IABF_ARCHIVE_YEARS = [2022, 2023, 2024, 2025]

/**
 * Years that had the fixed corps per-piece video admin pages
 * (WoH, QoH, GW, Becoming, Jazz). 2027+ will use new pieces — hide these
 * nav links outside these years.
 */
export const IABF_CORPS_VIDEO_YEARS = [2026]

/**
 * Years that have the dancer corps costume-order form (2026 repertoire).
 * 2027+ stays hidden until a new form exists — add the year here to reopen.
 */
export const IABF_CORPS_COSTUME_YEARS = [2026]

export function isOperableYear (year) {
  return IABF_OPERABLE_YEARS.includes(Number(year))
}

export function isArchiveYear (year) {
  return IABF_ARCHIVE_YEARS.includes(Number(year))
}

export function hasCorpsVideoAdmin (year) {
  return IABF_CORPS_VIDEO_YEARS.includes(Number(year))
}

export function hasCorpsCostumeOrder (year) {
  return IABF_CORPS_COSTUME_YEARS.includes(Number(year))
}

/** Label for the year switcher dropdown. */
export function adminYearLabel (year) {
  const y = Number(year)
  if (y === 2026) {
    return '2026 (wrap-up)'
  }
  if (y === IABF_DANCER_YEAR) {
    return `${y} (current)`
  }
  if (y === IABF_REGISTRATION_YEAR && y !== IABF_DANCER_YEAR) {
    return `${y} (registration)`
  }
  if (isArchiveYear(y)) {
    return `${y} (archive)`
  }
  return String(y)
}

// --- Shared (not year-scoped) -------------------------------------------------

/** Stable person identity across festival years. */
export const IABF_PEOPLE_COLLECTION = 'iabf-people'

/** Multi-year accounting (rows carry festivalYear). */
export const IABF_ACCOUNTING_PRODUCT_LINES = 'iabf-accounting-product-lines'
export const IABF_ACCOUNTING_TRANSACTIONS = 'iabf-accounting-transactions'
export const IABF_ACCOUNTING_META = 'iabf-accounting-meta'

/** Physical festival stock (bins, Becoming loaners). Tutus stay in costume-inventory. */
export const IABF_FESTIVAL_INVENTORY_COLLECTION = 'iabf-festival-inventory'

/** Shared costume closet dancers can browse and borrow. */
export const IABF_COSTUME_CLOSET_COLLECTION = 'iabf-costume-closet'

/** Closet loans + dancer purchase ledger (rows carry festivalYear). */
export const IABF_CLOSET_LOANS_COLLECTION = 'iabf-closet-loans'
export const IABF_DANCER_LEDGER_COLLECTION = 'iabf-dancer-ledger'

// --- Year-scoped helpers -----------------------------------------------------

/**
 * Year-scoped enrollment (dancer) collection.
 * 2024 used the unsuffixed name historically.
 * 2022–2023 have no enrollment collection (people.yearsAttended only).
 */
export function dancersCollection (year = IABF_DANCER_YEAR) {
  const y = Number(year)
  if (y === 2022 || y === 2023) {
    return null
  }
  if (y === 2024) {
    return 'iabf-dancers'
  }
  return `iabf-dancers-${y}`
}

/** Year-scoped faculty (teachers) — not dancer tickets, not room inventory. */
export function facultyCollection (year = IABF_DANCER_YEAR) {
  const y = Number(year)
  if (y === 2022 || y === 2023) {
    return null
  }
  return `iabf-faculty-${y}`
}

/**
 * Standard year-scoped collection: `iabf-{name}-{year}`.
 *
 * Logical names (examples):
 *   pieces, corps-pieces, travel-details, carpools, classes, config,
 *   store-items, store-orders, addon-services, showcase-feedback,
 *   guest-registrations, canceled-dancers,
 *   woh-videos, qoh-videos, gw-videos, becoming-videos, jazz-videos
 *
 * Do not use for people or accounting (shared).
 */
export function col (name, year = IABF_DANCER_YEAR) {
  return `iabf-${name}-${year}`
}

/** Firebase Storage path prefix: `iabf-{name}-{year}/…` */
export function storagePrefix (name, year = IABF_DANCER_YEAR) {
  return `iabf-${name}-${year}`
}

/** Stripe Checkout metadata.flow values. */
export function stripeFlow (name, year = IABF_DANCER_YEAR) {
  return `iabf-${name}-${year}`
}

/** Profile fields that live on the person and dual-write to the enrollment. */
export const IABF_PERSON_PROFILE_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'last4',
  // Mailing / contact address (registration + profile)
  'addressStreet',
  'addressStreet2',
  'addressCity',
  'addressState',
  'addressZip',
  'addressCountry',
  'emergencyContactName',
  'emergencyContactPhone',
  'emergencyContactRelationship',
  'emergencyContactEntered',
  'diet',
  'dietDetails',
  'gluten',
  'foodsNotConsumed',
  'foodAllergies',
  'coffee',
  'coffeeMakerStock',
  'dietaryNotes',
  'dietaryRestrictionsEntered'
]

/**
 * Collections that were unversioned in 2026 and are copy-migrated to
 * year-suffixed names. Old unversioned collections are left in place
 * (no deletes) until we explicitly retire them later.
 */
export const IABF_VERSIONED_IN_PHASE2 = [
  'guest-registrations',
  'canceled-dancers',
  'woh-videos',
  'qoh-videos',
  'gw-videos',
  'becoming-videos',
  'jazz-videos'
]
